import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocationOn as LocationIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Part {
  id: string;
  name: string;
  brand: string;
  location: string;
  supplier: string;
  price: number;
  availability: number;
  category: string;
}

export const PartsSearch: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [brand, setBrand] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [supplier, setSupplier] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Mock data - replace with API call
  const parts: Part[] = [
    {
      id: '1',
      name: 'Brake Pad Set',
      brand: 'Bosch',
      location: 'Sandton',
      supplier: 'Parts Corp SA',
      price: 1499.99,
      availability: 45,
      category: 'Brakes',
    },
    {
      id: '2',
      name: 'Oil Filter',
      brand: 'Mann-Filter',
      location: 'Midrand',
      supplier: 'AutoZone SA',
      price: 249.99,
      availability: 32,
      category: 'Filters',
    },
    {
      id: '3',
      name: 'Spark Plug Set',
      brand: 'NGK',
      location: 'Centurion',
      supplier: 'SpareZone',
      price: 599.99,
      availability: 20,
      category: 'Ignition',
    },
  ];

  const handleSearch = () => {
    // Implement search logic
    console.log('Searching for:', searchTerm);
  };

  const handleFilterChange = (
    event: SelectChangeEvent<string>,
    type: 'brand' | 'location' | 'supplier' | 'category'
  ) => {
    const value = event.target.value;
    switch (type) {
      case 'brand':
        setBrand(value);
        break;
      case 'location':
        setLocation(value);
        break;
      case 'supplier':
        setSupplier(value);
        break;
      case 'category':
        setCategory(value);
        break;
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Parts Search
        </Typography>

        {/* Search Bar */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search parts by name, brand, or part number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowFilters(!showFilters)}>
                          <FilterIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
              </Grid>
            </Grid>

            {/* Filters */}
            {showFilters && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={brand}
                      label="Brand"
                      onChange={(e) => handleFilterChange(e, 'brand')}
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      <MenuItem value="bosch">Bosch</MenuItem>
                      <MenuItem value="ngk">NGK</MenuItem>
                      <MenuItem value="mann-filter">Mann-Filter</MenuItem>
                      <MenuItem value="gabriel">Gabriel</MenuItem>
                      <MenuItem value="willard">Willard</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={location}
                      label="Location"
                      onChange={(e) => handleFilterChange(e, 'location')}
                    >
                      <MenuItem value="">All Locations</MenuItem>
                      <MenuItem value="sandton">Sandton</MenuItem>
                      <MenuItem value="midrand">Midrand</MenuItem>
                      <MenuItem value="centurion">Centurion</MenuItem>
                      <MenuItem value="pretoria">Pretoria</MenuItem>
                      <MenuItem value="johannesburg">Johannesburg</MenuItem>
                      <MenuItem value="randburg">Randburg</MenuItem>
                      <MenuItem value="roodepoort">Roodepoort</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select
                      value={supplier}
                      label="Supplier"
                      onChange={(e) => handleFilterChange(e, 'supplier')}
                    >
                      <MenuItem value="">All Suppliers</MenuItem>
                      <MenuItem value="parts-corp">Parts Corp SA</MenuItem>
                      <MenuItem value="autozone">AutoZone SA</MenuItem>
                      <MenuItem value="sparezone">SpareZone</MenuItem>
                      <MenuItem value="masterparts">MasterParts</MenuItem>
                      <MenuItem value="goldwagen">Goldwagen</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => handleFilterChange(e, 'category')}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      <MenuItem value="brakes">Brakes</MenuItem>
                      <MenuItem value="engine">Engine Parts</MenuItem>
                      <MenuItem value="suspension">Suspension</MenuItem>
                      <MenuItem value="electrical">Electrical</MenuItem>
                      <MenuItem value="filters">Filters</MenuItem>
                      <MenuItem value="batteries">Batteries</MenuItem>
                      <MenuItem value="oils">Oils & Lubricants</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Grid container spacing={3}>
          {parts.map((part) => (
            <Grid item xs={12} md={6} lg={4} key={part.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {part.name}
                  </Typography>
                  <Typography color="textSecondary" gutterBottom>
                    Brand: {part.brand}
                  </Typography>
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Chip
                      icon={<LocationIcon />}
                      label={part.location}
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      icon={<StoreIcon />}
                      label={part.supplier}
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      icon={<ShippingIcon />}
                      label={`${part.availability} in stock`}
                    />
                  </Box>
                  <Typography variant="h5" color="primary" gutterBottom>
                    R {part.price.toFixed(2)}
                  </Typography>
                  <Button variant="contained" color="primary" fullWidth>
                    Order Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </DashboardLayout>
  );
}; 